import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import { PasswordInput } from "./PasswordInput";
import { Label } from "./ui/label";

const EMPTY = { current_password: "", new_password: "", confirm: "" };

export const ChangePasswordCard = () => {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) return toast.error("Those new passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      setForm(EMPTY);
      toast.success("Password changed — use it the next time you sign in");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const field = (key, label) => (
    <div>
      <Label className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </Label>
      <PasswordInput
        required
        value={form[key]}
        onChange={set(key)}
        testId={`change-password-${key.replace("_", "-")}`}
        className="mt-2"
      />
    </div>
  );

  return (
    <form
      onSubmit={submit}
      data-testid="change-password-card"
      className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm"
    >
      <div className="flex items-center gap-3">
        <span className="grid h-9 w-9 place-items-center rounded-md bg-slate-900 text-white">
          <KeyRound className="h-4 w-4" />
        </span>
        <div>
          <h2 className="font-display text-lg font-extrabold tracking-tight text-slate-900">
            Change your password
          </h2>
          <p className="text-sm text-slate-500">
            At least 8 characters. We email you a confirmation each time it changes.
          </p>
        </div>
      </div>
      <div className="mt-7 grid gap-5 sm:grid-cols-3">
        {field("current_password", "Current password")}
        {field("new_password", "New password")}
        {field("confirm", "Confirm new password")}
      </div>
      <Button
        type="submit"
        disabled={busy}
        data-testid="change-password-submit"
        className="mt-7 h-11 rounded-full bg-blue-600 px-7 font-semibold transition-colors duration-200 hover:bg-blue-700 active:scale-95"
      >
        {busy ? "Updating…" : "Update password"}
      </Button>
    </form>
  );
};
